import React from 'react';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';

const BS_CURRENCIES = new Set(['bs', 'ves', 'vef', 'bolivar', 'bolivares', 'bolívares', 'bolívar']);

export function normalizeAuditCurrency(currency) {
    const value = String(currency || 'dollars')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    if (BS_CURRENCIES.has(value)) return 'bs';
    return 'usd';
}

export function formatUsd(amount) {
    if (amount == null || Number.isNaN(Number(amount))) return null;
    return `$${Number(amount).toFixed(2)}`;
}

export function formatBs(amount) {
    if (amount == null || Number.isNaN(Number(amount))) return null;
    return `Bs ${Number(amount).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function getAuditPriceTooltip(conversion) {
    if (!conversion) return undefined;
    if (conversion.conversionError) return conversion.conversionError;
    if (!conversion.converted) return undefined;
    const parts = [
        `Original: ${formatBs(conversion.originalAmount) || conversion.originalAmount}`,
        conversion.exchangeRate ? `Tasa BCV: Bs ${Number(conversion.exchangeRate).toFixed(2)} / USD` : null,
        conversion.exchangeRateDate ? `Fecha tasa: ${dayjs(conversion.exchangeRateDate).format('DD/MM/YYYY')}` : null,
    ].filter(Boolean);
    return parts.join(' · ');
}

/** Resolve USD label from enriched record fields, with fallback when API lacks conversion. */
export function resolveAuditPriceUsd(price, currency, priceUsd, priceConversion) {
    if (priceUsd != null && !Number.isNaN(Number(priceUsd))) {
        return { usd: Number(priceUsd), conversion: priceConversion };
    }
    if (price == null || price === '') return { usd: null, conversion: priceConversion };
    if (normalizeAuditCurrency(currency) === 'usd') {
        return { usd: Number(price), conversion: priceConversion };
    }
    return { usd: null, conversion: priceConversion || { conversionError: 'Sin conversión a USD disponible' } };
}

/** Format the raw original price using its native currency (Bs or plain number). */
export function formatAuditRawPrice(price, currency) {
    if (price == null || price === '' || Number.isNaN(Number(price))) return null;
    if (normalizeAuditCurrency(currency) === 'bs') {
        return formatBs(price);
    }
    return formatUsd(price);
}

// Small inline marker next to a price (e.g. "sin conversión", "Bs→USD").
const PriceMarker = ({ tone = 'neutral', children }) => (
    <span
        className={`ml-1.5 inline-flex items-center rounded px-1 py-px align-middle text-[10px] font-medium ${
            tone === 'warning' ? 'bg-warning-bg text-warning-fg' : 'bg-surface-100 text-surface-500'
        }`}>
        {children}
    </span>
);

export default function AuditPriceDisplay({ price, currency, priceUsd, priceConversion, promoPrice, promoPriceUsd, promoPriceConversion, isPromo, size = 'default' }) {
    const main = resolveAuditPriceUsd(price, currency, priceUsd, priceConversion);
    const promo = isPromo ? resolveAuditPriceUsd(promoPrice, currency, promoPriceUsd, promoPriceConversion) : null;

    const mainLabel = formatUsd(main.usd);
    const mainTip = getAuditPriceTooltip(main.conversion);
    const textClass = size === 'large' ? 'text-2xl font-semibold tracking-tight tabular-nums' : 'text-sm font-medium tabular-nums';

    // No real USD value. Fall back to the original price + currency if one exists,
    // showing a muted "sin conversión" hint instead of a fake "$" or a bare "—".
    if (!mainLabel) {
        const rawLabel = formatAuditRawPrice(price, currency);
        if (rawLabel) {
            const rawTip = main.conversion?.conversionError || 'Sin conversión a USD disponible';
            const rawPromoLabel = isPromo ? formatAuditRawPrice(promoPrice, currency) : null;
            return (
                <div className="inline-flex flex-col gap-0.5">
                    <Tooltip title={rawTip}>
                        <span>
                            <span className={`${textClass} text-surface-900`}>{rawLabel}</span>
                            <PriceMarker>sin conversión</PriceMarker>
                        </span>
                    </Tooltip>
                    {rawPromoLabel && <span className="text-xs text-violet-fg tabular-nums">Promo: {rawPromoLabel}</span>}
                </div>
            );
        }
        // Genuinely no price at all.
        return (
            <Tooltip title="Precio no disponible">
                <span className={`${textClass} text-surface-400`}>—</span>
            </Tooltip>
        );
    }

    const mainNode = (
        <span>
            <span className={`${textClass} text-surface-900`}>{mainLabel}</span>
            {main.conversion?.converted && <PriceMarker tone="warning">Bs→USD</PriceMarker>}
        </span>
    );

    return (
        <div className="inline-flex flex-col gap-0.5">
            {mainTip ? <Tooltip title={mainTip}>{mainNode}</Tooltip> : mainNode}
            {promo?.usd != null && (
                <Tooltip title={getAuditPriceTooltip(promo.conversion)}>
                    <span className="text-xs text-violet-fg tabular-nums">Promo: {formatUsd(promo.usd)}</span>
                </Tooltip>
            )}
        </div>
    );
}
